import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import './AdminPanel.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function AdminPanel({ onClose }) {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    full_name: '',
    password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/auth/users`);
      setUsers(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/register`, newUser);
      setShowAddUser(false);
      setNewUser({ username: '', email: '', full_name: '', password: '' });
      fetchUsers();
      alert('User created successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/auth/users/${userId}`, {
        is_active: !isActive
      });
      fetchUsers();
    } catch (err) {
      alert('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/auth/users/${userId}`);
      fetchUsers();
      alert('User deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  if (!user?.is_admin) {
    return null;
  }

  return (
    <div className="admin-panel-overlay" onClick={onClose}>
      <div className="admin-panel" onClick={(e) => e.stopPropagation()}>
        <div className="admin-panel-header">
          <h2>👥 User Management</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="admin-panel-content">
          {error && <div className="error-message">{error}</div>}

          <div className="admin-actions">
            <button 
              className="add-user-btn"
              onClick={() => setShowAddUser(!showAddUser)}
            >
              ➕ Add New User
            </button>
          </div>

          {showAddUser && (
            <div className="add-user-form">
              <h3>Create New User</h3>
              <form onSubmit={handleAddUser}>
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="submit-btn">Create User</button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowAddUser(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Full Name</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className={!u.is_active ? 'inactive' : ''}>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td>{u.full_name || '-'}</td>
                      <td>
                        <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                          {u.is_active ? '✓ Active' : '✕ Inactive'}
                        </span>
                      </td>
                      <td>
                        {u.is_admin && <span className="admin-badge">Admin</span>}
                        {!u.is_admin && <span className="user-badge">User</span>}
                      </td>
                      <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="toggle-btn"
                            onClick={() => handleToggleActive(u.id, u.is_active)}
                            title={u.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {u.is_active ? '🔒' : '🔓'}
                          </button>
                          {u.id !== user.id && (
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteUser(u.id, u.username)}
                              title="Delete user"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
